import React, { useEffect, useRef } from 'react';

import { Button, Icon, Space, Typography } from '@sravni/react-design-system';
import { ArrowRight, Star } from '@sravni/react-icons';
import { useBoolean } from '@sravni/react-utils';
import { checkIfMfo } from '@sravni/ugc/lib/utils/checkOrganizationsType';

import {
  PRODUCT_LIST,
  PRODUCT_LIST_MFO,
  SPECIFIC_PRODUCT_LIST,
  SPECIFIC_PRODUCT_LIST_MFO,
} from '../../../../constants/routes';
import { IServiceOption } from '../../../../interfaces/reviews';
import { createLinkProps } from '../../../../utils/routing';
import { IProductsAndSqueezeBlock } from '../ProductsAndSqueezeBlock';
import { ProductsModal } from '../ProductsModal';

import styles from './products-block.module.scss';

interface IProps
  extends Pick<IProductsAndSqueezeBlock, 'product' | 'typePage' | 'organizationAlias' | 'type' | 'isMobile'> {
  servicesOptions: IServiceOption[];
  handleCloseProductModal: () => void;
}

export const ProductsBlock: React.FC<IProps> = (props) => {
  const { product, organizationAlias, type, isMobile, servicesOptions, handleCloseProductModal } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<HTMLAnchorElement[]>([]);
  const buttonMoreRef = useRef<HTMLButtonElement>(null);

  const [showModal, setShowModal] = useBoolean(false);

  const isServicePage = Boolean(product);
  const isMfo = checkIfMfo(type);

  useEffect(() => {
    if (isMobile) return;

    const calculateVisibleServices = () => {
      if (!containerRef.current) return;

      const moreBtnWidth = 80;
      const gapWidth = 8;
      const containerWidth = containerRef.current.offsetWidth - (moreBtnWidth + gapWidth);

      let totalWidth = 0;

      for (let i = 0; i < servicesOptions?.length; i++) {
        const button = buttonRefs.current[i];
        if (!button) continue;

        const btnWidth = button.offsetWidth;

        if (totalWidth + btnWidth + gapWidth < containerWidth) {
          totalWidth += btnWidth + gapWidth;
          buttonMoreRef.current.style.marginLeft = `${gapWidth}px`;
        } else {
          innerContainerRef.current.style.width = `${totalWidth}px`;
          buttonMoreRef.current.style.marginLeft = `0px`;
          break;
        }
      }
    };

    calculateVisibleServices();

    window.addEventListener('resize', calculateVisibleServices);

    return () => {
      window.removeEventListener('resize', calculateVisibleServices);
    };
  }, [isMobile, servicesOptions]);

  if (!servicesOptions?.length) return <div ref={containerRef} />;

  const getTagLink = (service, productId?: string) => {
    const currentId = productId || service.id;
    const mfoPattern = currentId ? SPECIFIC_PRODUCT_LIST_MFO : PRODUCT_LIST_MFO;
    const commonPattern = currentId ? SPECIFIC_PRODUCT_LIST : PRODUCT_LIST;

    return createLinkProps('/list', isMfo ? mfoPattern : commonPattern, {
      type,
      organization: organizationAlias,
      product: service.alias || product?.alias,
      ...((product || (productId && productId !== 'All')) && { specificProduct: currentId }),
    });
  };

  const handleCloseModal = () => {
    setShowModal.off();
    handleCloseProductModal();
  };

  const renderServiceTag = (service, index) => {
    const { as } = getTagLink(service);

    return (
      <a href={as} ref={(el) => (buttonRefs.current[index] = el)}>
        <Button key={`service-link-${service.value}`} size={36}>
          {service.label || service.name}
          {service.rating > 0 && (
            <>
              <Icon icon={<Star />} className={'h-color-D40'} size={16} />
              <Typography.Text size={10} className={'h-color-D40'}>
                {service.rating > 0 && service.rating.toFixed(1)}
              </Typography.Text>
            </>
          )}
        </Button>
      </a>
    );
  };

  const moreButton = (
    <Button ref={buttonMoreRef} onClick={setShowModal.on} size={36} variant={'outlined'}>
      Ещё <Icon icon={<ArrowRight />} />
    </Button>
  );

  const listToShow = isMobile ? servicesOptions?.slice(0, 3) : servicesOptions;

  return (
    <div className={styles.wrapper}>
      <Typography.Text strong className={'h-mb-16'}>
        Отзывы по {product ? 'продуктам' : 'услугам'}
      </Typography.Text>
      <Space ref={containerRef} className={styles.tagsWrapper} align="start">
        <Space ref={innerContainerRef} size={8} className={styles.tagsInner}>
          {listToShow?.map((service, index) => renderServiceTag(service, index))}
          {isMobile && moreButton}
        </Space>
        {!isMobile && moreButton}
      </Space>
      <ProductsModal
        isModalOpened={showModal}
        handleClose={handleCloseModal}
        servicesOptions={servicesOptions}
        modalName={`Все ${product ? 'продукты' : 'услуги'}`}
        getTagLink={getTagLink}
        isServicePage={isServicePage}
        type={type}
      />
    </div>
  );
};
